import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Camera, Image as ImageIcon, X, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUploader
 *  - In CREATE mode (no workLogId): keeps files in local state. Parent calls
 *    `getPending()` via ref to flush them after the parte is saved.
 *  - In EDIT mode (workLogId): fetches existing photos, uploads immediately,
 *    supports soft-delete.
 */
export default function PhotoUploader({ workLogId = null, pendingRef }) {
  const [pending, setPending] = useState([]); // [{file, previewUrl}]
  const [existing, setExisting] = useState([]); // [{id, ...}]
  const [thumbs, setThumbs] = useState({}); // id -> objectURL
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null); // url being viewed
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Expose pending files so the parent can flush them after save
  useEffect(() => {
    if (pendingRef) {
      pendingRef.current = {
        getPending: () => pending.map((p) => p.file),
        clearPending: () => {
          pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
          setPending([]);
        },
      };
    }
  }, [pending, pendingRef]);

  // Load existing photos on edit mode
  useEffect(() => {
    if (!workLogId) return;
    api.get(`/work-logs/${workLogId}/photos`).then((r) => {
      setExisting(r.data || []);
    }).catch(() => {});
  }, [workLogId]);

  // Fetch thumbnails for existing photos as blob URLs (auth header friendly)
  useEffect(() => {
    let cancelled = false;
    const fetchThumbs = async () => {
      const next = { ...thumbs };
      for (const ph of existing) {
        if (next[ph.id]) continue;
        try {
          const res = await api.get(`/files/${ph.id}`, { responseType: "blob" });
          if (cancelled) return;
          next[ph.id] = URL.createObjectURL(res.data);
        } catch (_) {
          // ignore individual failures
        }
      }
      if (!cancelled) setThumbs(next);
    };
    if (existing.length) fetchThumbs();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing]);

  // Cleanup object URLs on unmount
  useEffect(() => () => {
    pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    Object.values(thumbs).forEach((url) => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!incoming.length) return;
    // size validation
    const tooBig = incoming.find((f) => f.size > 12 * 1024 * 1024);
    if (tooBig) {
      toast.error(`${tooBig.name}: máximo 12MB`);
      return;
    }
    if (workLogId) {
      // Edit mode: upload immediately
      setUploading(true);
      try {
        const fd = new FormData();
        incoming.forEach((f) => fd.append("files", f));
        const res = await api.post(`/work-logs/${workLogId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${res.data.count} foto${res.data.count > 1 ? "s" : ""} subida${res.data.count > 1 ? "s" : ""}`);
        const list = await api.get(`/work-logs/${workLogId}/photos`);
        setExisting(list.data || []);
      } catch (e) {
        toast.error("Error subiendo fotos");
      } finally {
        setUploading(false);
      }
    } else {
      // Create mode: hold locally
      const items = incoming.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
        localId: `${file.name}-${file.lastModified}-${file.size}`,
      }));
      setPending((prev) => [...prev, ...items]);
    }
  };

  const removePending = (localId) => {
    setPending((prev) => {
      const item = prev.find((p) => p.localId === localId);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  };

  const removeExisting = async (id) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/files/${id}`);
      setExisting((prev) => prev.filter((p) => p.id !== id));
      if (thumbs[id]) {
        URL.revokeObjectURL(thumbs[id]);
        setThumbs((t) => {
          const n = { ...t };
          delete n[id];
          return n;
        });
      }
      toast.success("Foto eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const totalCount = existing.length + pending.length;

  return (
    <section className="brutal-card p-5 space-y-4" data-testid="photo-uploader">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display font-black text-xl">
          Fotos del trabajo
          {totalCount > 0 && (
            <span className="ml-2 text-sm font-semibold text-slate-500">({totalCount})</span>
          )}
        </h2>
        <div className="flex gap-2">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            data-testid="photo-camera-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="photo-gallery-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            data-testid="photo-camera-btn"
            disabled={uploading}
            className="brutal-btn-dark h-10 px-3 inline-flex items-center gap-2 text-xs"
          >
            <Camera size={14} /> Cámara
          </button>
          <button
            type="button"
            onClick={() => galleryInputRef.current?.click()}
            data-testid="photo-gallery-btn"
            disabled={uploading}
            className="brutal-btn-ghost h-10 px-3 inline-flex items-center gap-2 text-xs"
          >
            <ImageIcon size={14} /> Galería
          </button>
        </div>
      </div>

      {!workLogId && (
        <p className="text-xs text-slate-500">
          Las fotos se subirán cuando guardes el parte.
        </p>
      )}
      {uploading && (
        <div className="text-xs text-slate-600 inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Subiendo...
        </div>
      )}

      {/* Grid */}
      {totalCount === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm">
          Aún no hay fotos. Añade una con la cámara o la galería.
        </div>
      ) : (
        <ul className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {existing.map((ph) => (
            <li key={ph.id} className="relative group aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
              {thumbs[ph.id] ? (
                <button
                  type="button"
                  className="w-full h-full"
                  onClick={() => setViewer(thumbs[ph.id])}
                  data-testid={`photo-thumb-${ph.id}`}
                >
                  <img
                    src={thumbs[ph.id]}
                    alt={ph.original_filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <Loader2 size={20} className="animate-spin" />
                </div>
              )}
              <button
                type="button"
                onClick={() => removeExisting(ph.id)}
                data-testid={`photo-remove-${ph.id}`}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 border border-slate-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
          {pending.map((p) => (
            <li key={p.localId} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-dashed border-[#F5A623] bg-orange-50">
              <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
              <span className="absolute bottom-1 left-1 bg-[#F5A623] text-white text-[9px] font-bold uppercase px-1.5 py-0.5 rounded">
                Pendiente
              </span>
              <button
                type="button"
                onClick={() => removePending(p.localId)}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 border border-slate-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Viewer modal */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setViewer(null)}
          data-testid="photo-viewer"
        >
          <button
            type="button"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            onClick={() => setViewer(null)}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
          <img
            src={viewer}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
